import { CreateAccountResponse } from "@/features/auth/types";
import type {
  ConfirmationResponse,
  ConfirmationResponseData,
  CreateAccountMirrorResponse,
  MatchingUuidEmailResponse
} from "@/features/register/types";
import { fetchServer } from "@/features/utils/fetch";
import { getFullName } from "@/features/utils/functions";
import type {
  AccountCreate,
  AccountCreateMirror,
  NaturalEntityModify
} from "@/features/utils/types";
import type {
  AliasDelete,
  FullCountry,
  NaturalPersonMissingFields,
  StaticActivity,
  StaticCSP,
  StaticData,
  StaticLegalStatus,
  StaticNace,
  StaticNatureMandate,
  StaticMandateService,
  UserType,
  legal_form_type
} from "../types";
import {
  AliasMobileSchema,
  CreateAliasMobileData,
  DeleteAliasMobileData
} from "@/types/user/alias-mobile.types";
import { ApiError } from "@/utils/internal-code-handler";
import { urlWithLegalEntity } from "./utils";
import {
  AliasMailSchema,
  CreateAliasMailDto,
  DeleteAliasMailResponseDto
} from "@/types/user/alias-mail.types";

export class AuthApiClient {
  static async profile(): Promise<UserType> {
    const profile = await fetchServer("/user/profile", {
      //reloadOn401: true
    });
    const data = { ...(await profile.json()) };
    if (data?.naturalPerson) {
      const cachedFreshUser = {
        email: data?.naturalPerson?.AliasEmail?.[0]?.Mail || "",
        name: getFullName(data?.naturalPerson),
        avatar: ""
      };
      sessionStorage.setItem("user", JSON.stringify(cachedFreshUser));
    }

    return data;
  }

  static async editProfileMissingFields(
    data: NaturalPersonMissingFields
  ): Promise<ConfirmationResponse> {
    // @ts-expect-error Output
    return fetchServer("/user/profile/edit_missing_fields", {
      method: "PUT",
      body: data,
      reloadOn401: false
    });
  }

  static async editProfile(data: NaturalEntityModify): Promise<ConfirmationResponse> {
    // @ts-expect-error Output
    return fetchServer("/user/profile/edit", { method: "PUT", body: data, reloadOn401: false });
  }

  static async createAccount(data: AccountCreate): Promise<CreateAccountResponse> {
    const response = await fetchServer("/register/account", {
      method: "POST",
      body: data,
      reloadOn401: false
    });
    const jsonResponse = await response.json();
    if (jsonResponse.message === "Invalid OTP JWT.") {
      throw new Error(jsonResponse.message);
    }
    if (!response.ok) throw new Error("Error checkLogin");
    return jsonResponse;
  }

  static async createAccountMirror(
    data: AccountCreateMirror
  ): Promise<CreateAccountMirrorResponse> {
    const response = await fetchServer("/register/complete-account-mirror", {
      method: "POST",
      body: data,
      reloadOn401: false
    });
    if (!response.ok) throw new Error("Error createAccountMirror");
    const res = await response.json();
    return res;
  }

  static async putPartialProfile(data: NaturalEntityModify) {
    return fetchServer("/user/profile/edit", { method: "PUT", body: data });
  }

  static async getStaticData(locale: string): Promise<StaticData> {
    const response = await fetchServer("/public/static_data/" + locale.toLowerCase(), {
      reloadOn401: false
    });
    return await response.json();
  }

  static async getStaticCountries(locale: string): Promise<{ countries: FullCountry[] }> {
    const response = await fetchServer("/public/countries/" + locale.toLowerCase(), {
      reloadOn401: false
    });
    return await response.json();
  }

  static async getStaticCSP(locale: string): Promise<{ cspCodes: StaticCSP[] }> {
    const response = await fetchServer("/public/csp/" + locale.toLowerCase(), {
      reloadOn401: false
    });
    return await response.json();
  }

  static async getStaticActivities(locale: string): Promise<{ activities: StaticActivity[] }> {
    const response = await fetchServer("/public/activities/" + locale.toLowerCase(), {
      reloadOn401: false
    });
    return await response.json();
  }
  static async getActivitiesNace(locale: string): Promise<{ activities_nace: StaticActivity[] }> {
    const response = await fetchServer("/public/activities_nace/" + locale.toLowerCase(), {
      reloadOn401: false
    });
    return await response.json();
  }
  static async getStaticLegalStatus(locale: string): Promise<{ legalStatus: StaticLegalStatus[] }> {
    const response = await fetchServer("/public/legal_status/" + locale.toLowerCase(), {
      reloadOn401: false
    });
    return await response.json();
  }

  static async getStaticNace(locale: string): Promise<{ naceCodes: StaticNace[] }> {
    const response = await fetchServer("/public/nas/" + locale.toLowerCase(), {
      reloadOn401: false
    });
    return await response.json();
  }

  static async getLegalForms(): Promise<legal_form_type[]> {
    const response = await fetchServer("/public/legal_forms", {
      reloadOn401: false
    });
    return await response.json();
  }
  static async confirmMirror(
    email: string,
    mirror_uuid: string
  ): Promise<ConfirmationResponseData<MatchingUuidEmailResponse>> {
    const response = await fetchServer(
      `/public/login/matching/uuid_email?mirrorUuid=${mirror_uuid}&email=${encodeURIComponent(
        email
      )}`,
      {
        method: "GET",
        reloadOn401: false
      }
    );
    if (!response.ok) {
      throw new Error("error confirm mirror");
    }
    return await response.json();
  }
  static async getStaticNatureMandate(): Promise<StaticNatureMandate[]> {
    const response = await fetchServer(`/nature_mandate`, {
      method: "GET",
      reloadOn401: false
    });
    if (!response.ok) {
      throw new Error("error list nature mandate");
    }
    const res = await response.json();
    return res.data;
  }
  static async getStaticMandateService(): Promise<StaticMandateService[]> {
    const response = await fetchServer(`/mandate_service`, {
      method: "GET",
      reloadOn401: false
    });
    if (!response.ok) {
      throw new Error("error list mandate service");
    }
    const res = await response.json();
    return res.data;
  }
  static async deleteAlias(
    data: AliasDelete,
    idCompany: string | null
  ): Promise<ConfirmationResponse> {
    const response = await fetchServer(
      idCompany
        ? `/user/profile/alias/delete?contextLegalEntityUuid=${idCompany}`
        : "/user/profile/alias/delete",
      {
        method: "PUT",
        body: data,
        reloadOn401: false
      }
    );
    const success = response.status === 200;
    if (response.status !== 200) {
      throw new Error("Cannot be deleted");
    }
    return { success, message: "" };
  }

  static async getAllAliasMobile(legalEntityUuid: string | null): Promise<AliasMobileSchema[]> {
    const response = await fetchServer(urlWithLegalEntity("/aliasMobile", legalEntityUuid), {
      method: "GET",
      reloadOn401: false
    });

    if (!response.ok) {
      throw new ApiError(await response.json(), response.status);
    }

    const res = await response.json();
    return res.data;
  }

  static async deleteAliasMobile(
    aliasMobileUuid: string,
    legalEntityUuid: string | null
  ): Promise<DeleteAliasMobileData> {
    const response = await fetchServer(
      urlWithLegalEntity(`/aliasMobile/${aliasMobileUuid}`, legalEntityUuid),
      {
        method: "DELETE",
        reloadOn401: false
      }
    );

    if (!response.ok) {
      throw new ApiError(await response.json(), response.status);
    }

    const res = await response.json();
    return res.data;
  }

  static async createAliasMobile(
    aliasMobile: CreateAliasMobileData,
    legalEntityUuid: string | null
  ): Promise<AliasMobileSchema> {
    const response = await fetchServer(urlWithLegalEntity(`/aliasMobile`, legalEntityUuid), {
      method: "POST",
      reloadOn401: false,
      body: aliasMobile
    });

    if (!response.ok) {
      throw new ApiError(await response.json(), response.status);
    }

    const res = await response.json();
    return res.data;
  }

  static async setPublicAliasMobile(
    aliasMobileUuid: string,
    legalEntityUuid: string | null,
    isPublic: boolean
  ): Promise<AliasMobileSchema> {
    const response = await fetchServer(
      urlWithLegalEntity(`/aliasMobile/${aliasMobileUuid}`, legalEntityUuid),
      {
        method: "PATCH",
        reloadOn401: false,
        body: { public: isPublic }
      }
    );

    if (!response.ok) {
      throw new ApiError(await response.json(), response.status);
    }

    const res = await response.json();
    return res.data;
  }

  static async getAllAliasMail(legalEntityUuid: string | null): Promise<AliasMailSchema[]> {
    const response = await fetchServer(urlWithLegalEntity("/aliasMail", legalEntityUuid), {
      method: "GET",
      reloadOn401: false
    });

    if (!response.ok) {
      throw new ApiError(await response.json(), response.status);
    }

    const res = await response.json();
    return res.data;
  }

  static async createAliasMail(
    aliasMobile: CreateAliasMailDto,
    legalEntityUuid: string | null
  ): Promise<AliasMailSchema> {
    const response = await fetchServer(urlWithLegalEntity(`/aliasMail`, legalEntityUuid), {
      method: "POST",
      reloadOn401: false,
      body: aliasMobile
    });

    if (!response.ok) {
      throw new ApiError(await response.json(), response.status);
    }

    const res = await response.json();
    return res.data;
  }

  static async setPublicAliasMail(
    aliasMailUuid: string,
    legalEntityUuid: string | null,
    isPublic: boolean
  ): Promise<AliasMailSchema> {
    const response = await fetchServer(
      urlWithLegalEntity(`/aliasMail/${aliasMailUuid}`, legalEntityUuid),
      {
        method: "PATCH",
        reloadOn401: false,
        body: { public: isPublic }
      }
    );

    if (!response.ok) {
      throw new ApiError(await response.json(), response.status);
    }

    const res = await response.json();
    return res.data;
  }

  static async deleteAliasMail(
    aliasMailUuid: string,
    legalEntityUuid: string | null
  ): Promise<DeleteAliasMailResponseDto> {
    const response = await fetchServer(
      urlWithLegalEntity(`/aliasMail/${aliasMailUuid}`, legalEntityUuid),
      {
        method: "DELETE",
        reloadOn401: false
      }
    );

    if (!response.ok) {
      throw new ApiError(await response.json(), response.status);
    }

    const res = await response.json();
    return res.data;
  }
}
